// components/FilePreviewModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Alert } from "react-bootstrap";

const getName = (url = "") =>
  url.split("?")[0].split("#")[0].split("/").pop() || "Document";

const getMimeFromName = (name = "") => {
  const lower = name.toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(lower)) return "image/*";
  if (/\.pdf$/.test(lower)) return "application/pdf";
  return null;
};

const isImageMime = (mime) => mime && mime.startsWith("image/");
const isPdfMime = (mime) =>
  mime === "application/pdf" || mime === "application/x-pdf";

export default function FilePreviewModal({
  show,
  onHide,
  fileUrl,
  title,
  size = "xl",
}) {
  const [err, setErr] = useState(null);
  const [detectedMime, setDetectedMime] = useState(null);

  const filename = useMemo(() => title || getName(fileUrl), [fileUrl, title]);
  const extMime = useMemo(() => getMimeFromName(fileUrl), [fileUrl]);

  // Prefer detected mime (for blob:), then by extension, else generic
  const mime = detectedMime || extMime || "application/octet-stream";

  useEffect(() => {
    setErr(null);
    setDetectedMime(null);
  }, [fileUrl]);

  // If we get a blob: URL with unknown type, detect it
  useEffect(() => {
    if (fileUrl?.startsWith("blob:") && !extMime) {
      (async () => {
        try {
          const r = await fetch(fileUrl);
          const b = await r.blob();
          if (b.type) setDetectedMime(b.type);
        } catch {
          /* ignore */
        }
      })();
    }
  }, [fileUrl, extMime]);

  const canPreview = !!fileUrl && (isImageMime(mime) || isPdfMime(mime));

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size={size}
      contentClassName="d-flex flex-column vh-100"
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-truncate">{filename}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0 d-flex flex-column flex-grow-1">
        {err && (
          <Alert variant="danger" className="m-3">
            {err}
          </Alert>
        )}

        {!err && canPreview && isImageMime(mime) && (
          <div className="flex-grow-1 d-flex align-items-center justify-content-center bg-dark">
            <img
              src={fileUrl}
              alt={filename}
              title={filename}
              onError={() => setErr("Failed to load image.")}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        )}

        {!err && canPreview && isPdfMime(mime) && (
          <iframe
            title={filename}
            src={fileUrl}
            className="flex-grow-1"
            style={{ border: 0, width: "100%", height: "100%" }}
            onError={() => setErr("Failed to load document.")}
          />
        )}

        {!err && !canPreview && (
          <div className="p-3">
            <p className="mb-2">Preview not available for this file type.</p>
            {fileUrl && (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Open in new tab
              </a>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-between">
        <div className="text-truncate small text-muted">{mime}</div>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
